import { type ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, InputNumber, Modal, Select } from '@arco-design/web-react';

import { maasRequiredSelectRules } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';

import { CommandArgsNormalBlock, ensureCommandArgsNormalDefaults, resolveCommandArgsNormalPaths } from '../command-args-normal';
import type { MaasTaskFailurePolicy, MaasTaskRole, MaasTaskRunStrategy, MaasTaskTemplateStep } from './types';
import { buildDefaultTaskStepName, taskStepApiToForm, taskStepFormToApi } from './utils';

const FormItem = Form.Item;

const RUN_STRATEGY_OPTIONS: MaasTaskRunStrategy[] = ['Parallel', 'Sequential', 'Single', 'Stepwise'];
const FAILURE_POLICY_OPTIONS: MaasTaskFailurePolicy[] = ['Terminate', 'Skip', 'Pause'];
const ROLE_OPTIONS: MaasTaskRole[] = ['All', 'Head', 'Worker'];

/** 按角色拆分的节点组名称选项 */
export interface NodeGroupOptionsByRole {
  head: string[];
  worker: string[];
}

interface TaskStepModalProps {
  visible: boolean;
  initialValues?: MaasTaskTemplateStep;
  stepIndex: number;
  /** Head / Worker 节点组名称，用于按「运行节点角色」筛选「指定运行节点」 */
  nodeGroupOptionsByRole: NodeGroupOptionsByRole;
  /**
   * 可被当前步骤依赖的前置任务名称（不含自身及之后的步骤）。
   * 由步骤列表数组顺序决定。
   */
  dependableStepNames?: string[];
  /** 所有已有步骤名称，用于校验用户输入的名称是否重复。 */
  stepNames?: string[];
  /**
   * 「初始化命令和参数」脚本字段提示（如内置环境变量说明）。
   * 未传入时不展示。
   */
  scriptHint?: ReactNode;
  onOk: (step: MaasTaskTemplateStep) => void | Promise<void>;
  onCancel: () => void;
}

function resolveGroupNamesByRole(role: MaasTaskRole | undefined, options: NodeGroupOptionsByRole): string[] {
  if (role === 'Head') return options.head;
  if (role === 'Worker') return options.worker;
  // All 或其他：合并 head + worker
  const names = new Set<string>([...options.head, ...options.worker]);
  return [...names];
}

export function TaskStepModal({
  visible,
  initialValues,
  stepIndex,
  nodeGroupOptionsByRole,
  dependableStepNames = [],
  stepNames = [],
  scriptHint,
  onOk,
  onCancel,
}: TaskStepModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  // 稳定 paths 引用，避免 role 的 useWatch 重渲染导致 useEffect 误重置表单
  const commandPaths = useMemo(() => resolveCommandArgsNormalPaths('command'), []);
  const role = Form.useWatch('role', form) as MaasTaskRole | undefined;
  const autoStepName = buildDefaultTaskStepName(stepIndex);

  const runStrategyOptions = useMemo(
    () =>
      RUN_STRATEGY_OPTIONS.map((value) => ({
        label: t(`data-center:maas.task.runStrategy.${value}`, value),
        value,
      })),
    [t],
  );
  const failurePolicyOptions = useMemo(
    () =>
      FAILURE_POLICY_OPTIONS.map((value) => ({
        label: t(`data-center:maas.task.failurePolicy.${value}`, value),
        value,
      })),
    [t],
  );
  const roleOptions = useMemo(
    () =>
      ROLE_OPTIONS.map((value) => ({
        label: t(`data-center:maas.task.role.${value}`, value),
        value,
      })),
    [t],
  );
  const asyncOptions = useMemo(
    () => [
      { label: t('data-center:maas.task.step.async.true'), value: 'true' },
      { label: t('data-center:maas.task.step.async.false'), value: 'false' },
    ],
    [t],
  );
  const groupOptions = useMemo(() => {
    const names = resolveGroupNamesByRole(role, nodeGroupOptionsByRole);
    return names.map((name) => ({ label: name, value: name }));
  }, [nodeGroupOptionsByRole, role]);
  const dependsOnOptions = useMemo(() => dependableStepNames.map((name) => ({ label: name, value: name })), [dependableStepNames]);

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    if (initialValues) {
      const formValues = taskStepApiToForm(initialValues, { allowedDependsOn: dependableStepNames });
      form.setFieldsValue({
        ...formValues,
        // Select 选项值为字符串，表单内用 'true'/'false'
        async: formValues.async !== false ? 'true' : 'false',
      });
    } else {
      form.setFieldsValue({
        name: autoStepName,
        role: 'All',
        runStrategy: 'Parallel',
        failurePolicy: 'Terminate',
        async: 'false',
        dependsOn: [],
        retryCount: 0,
      });
      ensureCommandArgsNormalDefaults(form, commandPaths);
      form.setFieldValue(commandPaths.shellType, 'bash');
      form.setFieldValue(commandPaths.commands, 'bash -c');
    }
    // 仅在弹窗打开时回填；打开后忽略 initialValues 变化，避免详情轮询导致编辑中被重置
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate only when modal opens
  }, [visible]);

  const handleOk = async () => {
    try {
      const values = await form.validate();
      const commandFields = {
        _shellType: (form.getFieldValue(commandPaths.shellType) as 'bash' | 'sh' | undefined) ?? 'bash',
        _commandsText: String(form.getFieldValue(commandPaths.commands) ?? ''),
        _argsText: String(form.getFieldValue(commandPaths.args) ?? ''),
      };
      await onOk(
        taskStepFormToApi({
          ...values,
          async: String(values.async) !== 'false',
          command: commandFields,
        }),
      );
    } catch (error) {
      console.log(error);
      // 校验失败或提交失败时阻止 Modal 关闭
      return Promise.reject(error);
    }
  };

  return (
    <Modal
      visible={visible}
      title={initialValues ? t('data-center:maas.task.step.edit') : t('data-center:maas.task.step.add')}
      onOk={handleOk}
      onCancel={onCancel}
      style={{ width: 800 }}
      unmountOnExit
      maskClosable={false}
    >
      <div style={{ maxHeight: 'calc(100vh - 200px)', minHeight: '300px', height: '60vh', overflowY: 'auto' }}>
        <Form form={form} layout="vertical">
          <FormItem
            label={t('data-center:maas.task.step.name')}
            field="name"
            extra={t('data-center:maas.task.step.name.hint')}
            rules={[
              { required: true },
              {
                validator: (value, callback) => {
                  const name = String(value ?? '').trim();
                  const initialName = String(initialValues?.name ?? '').trim();
                  if (!name) return callback(t('common:required'));
                  return name !== initialName && stepNames.includes(name)
                    ? callback(t('data-center:maas.task.step.name.duplicate'))
                    : callback();
                },
              },
            ]}
          >
            <Input />
          </FormItem>
          <FormItem
            label={t('data-center:maas.task.step.async')}
            field="async"
            rules={maasRequiredSelectRules(t('data-center:maas.task.step.async'))}
            initialValue="false"
            extra={t('data-center:maas.task.step.async.hint')}
          >
            <Select options={asyncOptions} />
          </FormItem>
          <FormItem
            label={t('data-center:maas.task.step.dependsOn')}
            field="dependsOn"
            initialValue={[]}
            extra={t('data-center:maas.task.step.dependsOn.hint')}
          >
            <Select
              mode="multiple"
              allowClear
              options={dependsOnOptions}
              placeholder={t('data-center:maas.task.step.dependsOn.placeholder')}
              disabled={dependsOnOptions.length === 0}
            />
          </FormItem>
          <FormItem
            label={t('data-center:maas.task.step.type')}
            field="runStrategy"
            rules={maasRequiredSelectRules(t('data-center:maas.task.step.type'))}
          >
            <Select options={runStrategyOptions} />
          </FormItem>
          <FormItem
            label={t('data-center:maas.task.step.errorHandling')}
            field="failurePolicy"
            rules={maasRequiredSelectRules(t('data-center:maas.task.step.errorHandling'))}
          >
            <Select options={failurePolicyOptions} />
          </FormItem>
          <FormItem
            label={t('data-center:maas.task.step.role')}
            field="role"
            rules={maasRequiredSelectRules(t('data-center:maas.task.step.role'))}
          >
            <Select
              options={roleOptions}
              onChange={() => {
                // 切换运行节点角色时清空已选节点，避免跨角色残留
                form.setFieldValue('groups', undefined);
              }}
            />
          </FormItem>
          <FormItem label={t('data-center:maas.task.step.groups')} field="groups" extra={t('data-center:maas.task.step.groups.hint')}>
            <Select mode="multiple" allowClear options={groupOptions} placeholder={t('data-center:maas.task.step.groups.placeholder')} />
          </FormItem>
          <CommandArgsNormalBlock form={form} paths={commandPaths} blockMode="edit" options={{ required: true, scriptHint }} />
          <FormItem
            label={t('data-center:maas.task.step.timeout')}
            field="timeoutSeconds"
            extra={t('data-center:maas.task.step.timeout.hint')}
          >
            <InputNumber min={1} placeholder={t('data-center:maas.task.step.timeout.placeholder')} style={{ width: '100%' }} />
          </FormItem>
          <FormItem
            label={t('data-center:maas.task.step.retryCount')}
            field="retryCount"
            initialValue={0}
            extra={t('data-center:maas.task.step.retryCount.hint')}
          >
            <InputNumber min={-1} style={{ width: '100%' }} />
          </FormItem>
        </Form>
      </div>
    </Modal>
  );
}
